import { Link, useNavigate } from "react-router-dom";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import LockIcon from "@mui/icons-material/Lock";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { FaUserCircle } from "react-icons/fa";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccountCircle from "@mui/icons-material/AccountCircle";
import EditIcon from "@mui/icons-material/Edit";
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';
import SavingsIcon from '@mui/icons-material/Savings';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CommitIcon from '@mui/icons-material/Commit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import LayersIcon from '@mui/icons-material/Layers';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import Swal from 'sweetalert2';
// import "../../App.css";
import vtLogo from "../../assets/VT_logo.png";
import "./Sidebar.css";
import Logo from "../../assets/VT_logo.png";

const SidebarPro = ({
  isCollapsed,
  setIsCollapsed,
  data,
  permissions,
  roleName,
  onLogout,
  isSuperAdmin,
}) => {
  const navigate = useNavigate();
  const [isYesterdayDayEndPending, setIsYesterdayDayEndPending] = useState(false);
  const [pendingDayEndDate, setPendingDayEndDate] = useState(null);
  const branchId = data?.branchId || null;
  const branchName = data?.fk_staff_details_belongsTo_branch_details_branchId?.branchName || "";

  // ============================================================
  // SUPERADMIN BYPASS: Derived lock state.
  // SuperAdmin is NEVER locked, regardless of raw pending state.
  // ALL rendering, click handlers, and CSS use this variable.
  // ============================================================
  const isLocked = isSuperAdmin ? false : isYesterdayDayEndPending;

  /**
   * The pages the day-end lock lets through.
   *
   * Locking every route except the day-end is the point — but the exemption used
   * to be the single literal '/dayendprocess', so when the redesigned page was
   * added at /dayendprocess-v2 it was treated as just another blocked feature:
   * clicking it warned "complete the pending day-end first" and redirected to the
   * old page. The one screen that could clear the lock was itself locked.
   *
   * Any future day-end screen belongs in this list.
   */
  const DAY_END_PATHS = ['/dayendprocess', '/dayendprocess-v2'];
  const isDayEndPath = (path) => DAY_END_PATHS.includes(path);

  // Where the lock sends people. The alert used to redirect to the OLD page
  // while the new one is the screen actually in use, so the warning pushed the
  // operator away from the page that can clear the lock.
  const DAY_END_REDIRECT = '/dayendprocess-v2';

  // Receipts is normally locked with everything else. The one exception is a
  // loan disbursed with no matching funding receipt: Day-End v2 refuses to close
  // and tells the operator to record it under Ledger > Receipts — which the lock
  // then blocks. Neither screen can proceed without the other.
  //
  // Unlocked ONLY while that specific shortfall stands, and it closes itself:
  // recording the receipt clears unreceiptedAgencies, so Receipts locks again on
  // the next check. Every other pending-day-end case keeps it blocked.
  const RECEIPT_PATH = '/receipts';
  const [hasFundingShortfall, setHasFundingShortfall] = useState(false);

  // Payments is unlocked on exactly the same terms as Receipts, and for the same
  // reason. Day-End refuses to close while members' savings are out on loan and
  // tells the operator to record a "Repay Member Savings" PAYMENT — which the
  // lock then blocked, so neither screen could proceed without the other. That is
  // the same dead end the Receipts exemption above was written to fix; only the
  // agency half had been covered.
  //
  // Self-closing in the same way: recording the payment clears memberBorrowings,
  // so Payments locks again on the next check.
  //
  // This flag opens Receipts as well (see isPathAllowed): the money can come back
  // through either screen, so the operator must be able to reach both.
  const PAYMENT_PATH = '/payments';
  const [hasMemberBorrowings, setHasMemberBorrowings] = useState(false);

  // Ledger Report and Balance Sheet open with Receipts/Payments, and close with
  // them.
  //
  // Recording the entry that clears a funding block is a decision, not a
  // formality: the operator has to see which ledger holds the money before
  // choosing a source account. Ledger Report is the per-account Closing balance
  // and Balance Sheet is the same money grouped, so locking them while demanding
  // the entry is the same dead end as locking Payments was.
  //
  // READ-ONLY, so opening them cannot change any figure or close a day. They shut
  // again the moment the block clears, exactly like the other two.
  const LEDGER_VIEW_PATHS = ['/ledgerReport', '/balance-sheet'];

  // One rule for every gate — the click handlers and the cursor/greyed-out
  // styling all read this, so the sidebar can never look clickable while the
  // handler refuses, or the reverse.
  //
  // The member-savings block opens BOTH ledger screens, not just Payments.
  // Clearing it is not always a single "Repay Member Savings" payment: the money
  // comes back when an agency drawdown or share capital is RECEIPTED too, and the
  // savingsShortfall text on the same popup says exactly that. Unlocking only
  // Payments would leave the operator blocked again the moment the answer was a
  // receipt — the same dead end this whole fix is about.
  const hasFundingBlock = hasFundingShortfall || hasMemberBorrowings;

  const isPathAllowed = (path) =>
    isDayEndPath(path) ||
    (path === RECEIPT_PATH && hasFundingBlock) ||
    (path === PAYMENT_PATH && hasMemberBorrowings) ||
    (LEDGER_VIEW_PATHS.includes(path) && hasFundingBlock);

  const checkPendingDayEnd = async () => {
    console.log("========== SIDEBAR: checkPendingDayEnd ==========");
    console.log("Current roleName:", roleName, "| isSuperAdmin:", isSuperAdmin);

    if (isSuperAdmin) {
      console.log("SUPERADMIN detected → skipping ALL day-end restrictions");
      setIsYesterdayDayEndPending(false);
      setPendingDayEndDate(null);
      return;
    }

    if (!branchId) {
      console.log("No branchId → Sidebar UNLOCKED");
      setIsYesterdayDayEndPending(false);
      setPendingDayEndDate(null);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/checkYesterdayDayEnd`, {
        params: { branchId },
      });

      const result = response.data?.data;
      console.log("Day-end check result:", result);

      if (result && result.isPending) {
        setIsYesterdayDayEndPending(true);
        setPendingDayEndDate(result.pendingDate || null);
      } else {
        setIsYesterdayDayEndPending(false);
        setPendingDayEndDate(null);
      }
    } catch (error) {
      console.error('Error checking day end status:', error);
      setIsYesterdayDayEndPending(false);
      setPendingDayEndDate(null);
    }
  };

  // The same endpoint and the same field Day-End v2 blocks on
  // (DayendprocessV2.js), so the two screens cannot disagree about whether the
  // shortfall exists. Advisory only: the server still refuses the close with a
  // 409, so a failed read here can unlock Receipts but never close a day.
  const checkFundingShortfall = async () => {
    if (isSuperAdmin || !branchId) {
      setHasFundingShortfall(false);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/api/ledger/funds-available`, {
        params: { branchId },
      });
      const funds = response.data?.data || {};
      setHasFundingShortfall(
        Array.isArray(funds.unreceiptedAgencies) && funds.unreceiptedAgencies.length > 0
      );
      // Same response, no extra request — the endpoint already returns this
      // (fundsAvailable.js). Both flags are set together so Receipts and Payments
      // can be unlocked at the same time, which happens whenever a branch has an
      // unreceipted agency AND members' savings out on loan.
      setHasMemberBorrowings(Number(funds.memberBorrowings) > 0);
    } catch (error) {
      // Fail closed: an endpoint blip must not open Receipts or Payments on its own.
      setHasFundingShortfall(false);
      setHasMemberBorrowings(false);
    }
  };

  useEffect(() => {
    checkPendingDayEnd();
    checkFundingShortfall();
  }, [branchId, isSuperAdmin]);

  const logout = () => {
    onLogout();
    navigate("/");
  };

  const formatPendingDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  };

  const handleNavigation = async (path, entity) => {
    if (isLocked && !isPathAllowed(path)) {
      const displayDate = formatPendingDate(pendingDayEndDate);
      await Swal.fire({
        icon: 'warning',
        title: 'Day-End Required',
        text: displayDate
          ? `Pending Day-End Found for ${displayDate}. Please complete it before accessing other features.`
          : "Please complete the pending day-end process before accessing other features.",
        confirmButtonText: 'Go to Day-End',
        confirmButtonColor: '#004BB5',
        backdrop: true
      });

      navigate(DAY_END_REDIRECT);
      return;
    }

    navigate(path, { state: { entity } });

    if (window.innerWidth <= 768) {
      setIsCollapsed(true);
    }
  };

  const handleMenuClick = (path, entity) => {
    if (isLocked && !isPathAllowed(path)) {
      const displayDate = formatPendingDate(pendingDayEndDate);
      alert(displayDate
        ? `Pending Day-End Found for ${displayDate}. Please complete it before accessing other features.`
        : "Please complete the pending day-end process before accessing other features.");
      navigate(DAY_END_REDIRECT);
      return false;
    }

    if (window.innerWidth <= 768) {
      setIsCollapsed(true);
    }
    return true;
  };

  const menuItems = [
    {
      label: "Dashboard",
      path: "/dashboard",
      type: "link",
      page: "dashboard",
      icon: <DashboardIcon className="text-[20px]" />,
    },
    {
      label: "Master",
      type: "submenu",
      page: "master",
      icon: <SettingsIcon className="text-[20px]" />,
      groups: [
        {
          title: "Master Entities",
          children: [
            {
              label: "Customers",
              path: "/customers",
              type: "link",
              page: "customers",
              icon: <PeopleIcon className="text-[18px]" />,
            },
            {
              label: "Branches",
              path: "/branches",
              entity: "branches",
              page: "branches",
              icon: <LocationCityIcon className="text-[18px]" />,
            },
            {
              label: "Centers",
              path: "/centers",
              entity: "centers",
              page: "centers",
              icon: <PeopleIcon className="text-[18px]" />,
            },
            {
              label: "Staff",
              path: "/staff",
              entity: "staff",
              page: "staff",
              icon: <PeopleIcon className="text-[18px]" />,
            },
            {
              label: "Accounts",
              path: "/accounts",
              entity: "accounts",
              page: "accounts",
              icon: <FaUserCircle className="text-[18px]" />,
            },
          ]
        },
        {
          title: "Transactions Setup",
          children: [
            {
              label: "Fixed Deposit",
              path: "/fixeddeposite",
              type: "link",
              page: "fixeddeposite",
              icon: <GpsFixedIcon className="text-[18px]" />,
            },
            {
              label: "Chitfund",
              path: "/Chitfund",
              type: "link",
              page: "chitfund",
              icon: <EditIcon className="text-[18px]" />,
            },
            {
              label: "Balance Book",
              path: "/balance-book",
              type: "link",
              page: "balance_book",
              icon: <AccountBalanceIcon className="text-[18px]" />,
            },
          ]
        },
        {
          title: "Loan Management",
          children: [
            {
              label: "Business Loan",
              path: "/businessLoan",
              type: "link",
              page: "businessLoan",
              icon: <BusinessIcon className="text-[18px]" />,
            },
            {
              label: "Member Details",
              path: "/memberdetails",
              type: "link",
              page: "memberdetails",
              icon: <PeopleIcon className="text-[18px]" />,
            },
          ]
        },
        {
          title: "System Settings",
          children: [
            {
              label: "Roles",
              path: "/roles",
              entity: "roles",
              page: "roles",
              icon: <AdminPanelSettingsIcon className="text-[18px]" />,
            },
            {
              label: "Funding Agencies",
              path: "/funding-agencies",
              entity: "funding_agencies",
              page: "funding_agencies",
              icon: <AttachMoneyIcon className="text-[18px]" />,
            },
            {
              label: "Permissions",
              path: "/permissions",
              type: "link",
              page: "permissions",
              icon: <LockIcon className="text-[18px]" />,
            },
          ]
        }
      ]
    },
    {
      label: "Report",
      type: "submenu",
      page: "report",
      icon: <AssessmentIcon className="text-[20px]" />,
      groups: [
        {
          title: "Daily Operations",
          children: [
            {
              label: "Daily Report",
              path: "/daily-report",
              type: "link",
              page: "dailyReport",
              icon: <EventNoteIcon className="text-[18px]" />,
            },
            {
              label: "Daily Collection Report",
              path: "/DailyCollectionReport",
              type: "link",
              page: "dailyCollectionReport",
              icon: <AttachMoneyIcon className="text-[18px]" />,
            },
            {
              label: "CRO Collections Report",
              path: "/CroCollectionsReport",
              type: "link",
              page: "croCollectionsReport",
              icon: <PeopleIcon className="text-[18px]" />,
            },
            // {
            //   label: "Day End Process",
            //   path: "/dayendprocess",
            //   type: "link",
            //   page: "dayendprocess",
            //   icon: <EditCalendarIcon className="text-[18px]" />,
            // },
            {
              label: "Day End Process (New)",
              path: "/dayendprocess-v2",
              type: "link",
              page: "dayendprocess",
              icon: <EditCalendarIcon className="text-[18px]" />,
            },
          ]
        },
        {
          title: "Loan Reports",
          children: [
            {
              label: "Loan Demand Report",
              path: "/loandemandreport",
              type: "link",
              page: "loandemand",
              icon: <AccountBalanceIcon className="text-[18px]" />,
            },
            {
              label: "Future Demand Report",
              path: "/future-demand-report",
              type: "link",
              page: "future_demand_report",
              icon: <EventNoteIcon className="text-[18px]" />,
            },
            {
              label: "Overdue Report",
              path: "/overdue-report",
              type: "link",
              page: "overdue_report",
              icon: <HourglassEmptyIcon className="text-[18px]" />,
            },
            {
              label: "PAR Report",
              path: "/par-report",
              type: "link",
              page: "par_report",
              icon: <AssessmentIcon className="text-[18px]" />,
            },
            {
              label: "Foreclosure Report",
              path: "/foreclosure-report",
              type: "link",
              page: "foreclosure_report",
              icon: <CommitIcon className="text-[18px]" />,
            },
            {
              label: "Loan Outstanding Report",
              path: "/loan-outstanding-report",
              type: "link",
              page: "loan_outstanding_report",
              icon: <AccountBalanceWalletIcon className="text-[18px]" />,
            },
          ]
        },
        {
          title: "Savings & Deposits",
          children: [
            {
              label: "Savings Report",
              path: "/savingsreport",
              type: "link",
              page: "savings_report",
              icon: <LockIcon className="text-[18px]" />,
            },
            {
              label: "Demand Collection Report",
              path: "/demand_collection_report",
              type: "link",
              page: "demand_collection_report",
              icon: <CollectionsBookmarkIcon className="text-[18px]" />,
            },
            {
              label: "SFU Report",
              path: "/sfu_report",
              type: "link",
              page: "savings_follow_up_report",
              icon: <SavingsIcon className="text-[18px]" />,
            },
            {
              label: "Deposit and Withdraw Report",
              path: "/depositeandwithdraw_report",
              type: "link",
              page: "deposit_withdra_report",
              icon: <PublishedWithChangesIcon className="text-[18px]" />,
            },
            {
              label: "Security Deposit Report",
              path: "/security-deposit-report",
              type: "link",
              page: "security_deposit_report",
              icon: <AccountBalanceIcon className="text-[18px]" />,
            },
            {
              label: "Interest Report",
              path: "/interest-report",
              type: "link",
              page: "interest_report",
              icon: <MonetizationOnIcon className="text-[18px]" />,
            },
          ]
        },
        {
          title: "Other Activities",
          children: [
            {
              label: "Disbursed Amount Report",
              path: "/DisbursedAmountReport",
              type: "link",
              page: "disbursedAmountReport",
              icon: <MonetizationOnIcon className="text-[18px]" />,
            },
            {
              label: "Swaping Field Officer",
              path: "/swaping_field_officer",
              type: "link",
              page: "field_officer_swap",
              icon: <SwapHorizontalCircleIcon className="text-[18px]" />,
            },
            {
              label: "Master Data Report",
              path: "/master-data-report",
              type: "link",
              page: "master_data_report",
              icon: <AccountCircle className="text-[18px]" />,
            },
          ]
        }
      ]
    },
    {
      label: "Ledger",
      type: "submenu",
      page: "ledger",
      icon: <MenuBookIcon className="text-[20px]" />,
      groups: [
        {
          title: "Ledger Setup",
          children: [
            {
              label: "Add Accounts Group",
              path: "/accountGroup",
              type: "link",
              page: "account_group",
              icon: <GroupWorkIcon className="text-[18px]" />,
            },
            {
              label: "Ledger Group",
              path: "/ledgerGroup",
              type: "link",
              page: "ledger_group",
              icon: <LayersIcon className="text-[18px]" />,
            },
          ]
        },
        {
          title: "Ledger Actions",
          children: [
            {
              label: "Receipts",
              path: "/receipts",
              type: "link",
              page: "receipts",
              icon: <ReceiptIcon className="text-[18px]" />,
            },
            {
              label: "Payments",
              path: "/payments",
              type: "link",
              page: "payments",
              icon: <PaymentIcon className="text-[18px]" />,
            },
          ]
        },
        {
          title: "Financial Statements",
          children: [
            {
              label: "Ledger Report",
              path: "/ledgerReport",
              type: "link",
              page: "ledger_report",
              icon: <SummarizeIcon className="text-[18px]" />,
            },
            {
              label: "Balance Sheet",
              path: "/balance-sheet",
              type: "link",
              page: "balance_sheet",
              icon: <AccountBalanceWalletIcon className="text-[18px]" />,
            },
          ]
        }
      ]
    },
  ];

  // Section grouping for the label dividers in the image
  const sectionGroups = [
    {
      label: "OPERATIONS",
      pages: ["master"],
    },
    {
      label: "REPORTS",
      pages: ["report"],
    },
    {
      label: "LEDGER & FINANCE",
      pages: ["ledger"],
    },
  ];

  const hasPermission = (page) => {
    if (isSuperAdmin) return true;
    return permissions.find((permission) => permission.page === page && permission.view);
  };

  // Render a single sub-item (child of submenu)
  const renderSubItem = (subItem, subIndex) => (
    <MenuItem
      key={subIndex}
      icon={
        <span className="sub-item-icon-wrap">
          {subItem.icon}
        </span>
      }
      onClick={() =>
        subItem.entity
          ? handleNavigation(subItem.path, subItem.entity)
          : handleMenuClick(subItem.path)
      }
      style={{
        cursor: isLocked && !isPathAllowed(subItem.path) ? 'not-allowed' : 'pointer',
      }}
      component={
        !isLocked || isPathAllowed(subItem.path)
          ? <Link to={subItem.path} state={{ entity: subItem.entity }} className="link" />
          : <div />
      }
    >
      <span className="sub-item-dot" />
      <span className={`sub-item-label${isLocked && !isPathAllowed(subItem.path) ? ' locked-text' : ''}`}>
        {subItem.label}
      </span>
    </MenuItem>
  );

  // Render a top-level menu item (link type)
  const renderLinkItem = (item, index) => (
    <MenuItem
      key={index}
      icon={<span className="menu-icon-wrap">{item.icon}</span>}
      onClick={() => handleMenuClick(item.path)}
      style={{
        cursor: isLocked && !isPathAllowed(item.path) ? 'not-allowed' : 'pointer',
      }}
      component={
        (!isLocked || isPathAllowed(item.path))
          ? <Link to={item.path} className="link" />
          : <div />
      }
      className={item.page === "dashboard" ? "dashboard-menu-item" : ""}
    >
      <span className={`menu-item-label${isLocked && !isPathAllowed(item.path) ? ' locked-text' : ''}`}>
        {item.label}
      </span>
    </MenuItem>
  );

  // Render submenu
  const renderSubMenu = (item, index) => {
    if (!hasPermission(item.page)) return null;

    return (
      <SubMenu
        key={index}
        label={<span className="menu-item-label">{item.label}</span>}
        icon={<span className="menu-icon-wrap">{item.icon}</span>}
        style={{
          cursor: isLocked ? 'not-allowed' : 'pointer',
        }}
      >
        {item.groups
          ? item.groups.map((group, groupIndex) => {
            const groupChildren = group.children.filter((subItem) => {
              if (subItem.page === "branches" && !isSuperAdmin) return false;
              return hasPermission(subItem.page);
            });
            if (groupChildren.length === 0) return null;
            return (
              <div key={groupIndex} className="submenu-group-block">
                {!isCollapsed && (
                  <div className="submenu-group-label">{group.title}</div>
                )}
                <div className="submenu-connector-group">
                  {groupChildren.map((subItem, subIndex) =>
                    renderSubItem(subItem, subIndex)
                  )}
                </div>
              </div>
            );
          })
          : item.children
            ?.filter((subItem) => {
              if (subItem.page === "branches" && !isSuperAdmin) return false;
              return hasPermission(subItem.page);
            })
            .map((subItem, subIndex) => renderSubItem(subItem, subIndex))
        }
      </SubMenu>
    );
  };

  // Build grouped sections for rendering
  const renderMenuWithSections = () => {
    const elements = [];

    // Render Dashboard first (outside any section)
    const dashboardItem = menuItems.find(item => item.page === "dashboard");
    if (dashboardItem && hasPermission("dashboard")) {
      elements.push(renderLinkItem(dashboardItem, "dashboard-top"));
    }

    sectionGroups.forEach((section, sIdx) => {
      const sectionItems = menuItems.filter((item) =>
        section.pages.includes(item.page) && hasPermission(item.page)
      );
      if (sectionItems.length === 0) return;

      elements.push(
        <div key={`section-label-${sIdx}`} className="sidebar-section-label">
          {section.label}
        </div>
      );

      sectionItems.forEach((item, itemIdx) => {
        const key = `${sIdx}-${itemIdx}`;
        if (item.type === "link") {
          elements.push(renderLinkItem(item, key));
        } else if (item.type === "submenu") {
          elements.push(renderSubMenu(item, key));
        }
      });
    });

    return elements;
  };

  return (
    <Sidebar
      className="app sidebar-wrapper"
      collapsed={false}
      backgroundColor="#ffffff"
      rootStyles={{
        backgroundColor: "#ffffff",
        height: "100vh",
        position: "fixed",
        borderRight: "1px solid #E2E8F0",
        boxShadow: "2px 0 12px rgba(15,23,42,0.06)",
      }}
    >
      {/* ── Header ── */}
      <div className="sidebar-top-header">
        <div className="sidebar-logo-area">
          <div className="sidebar-logo-icon">
            <img src={vtLogo} className="sidebar-logo-v" alt="VT Logo" />
          </div>
          <div className="sidebar-logo-text">
            <span className="sidebar-logo-title">VIRTUAL</span>
            <span className="sidebar-logo-subtitle">TECHNOLOGIES</span>
          </div>
        </div>
      </div>

      {/* ── Scrollable Menu Area ── */}
      <div className="sidebar-scroll-area">
        <Menu>
          {renderMenuWithSections()}
        </Menu>
      </div>

      {/* ── Sticky Bottom: User Profile + Logout ── */}
      <div className="sidebar-bottom-area">
        <div className="sidebar-divider" />

        {/* User Profile Row */}
        {/* <div className="sidebar-user-row">
          <div className="sidebar-user-avatar">
            <AccountCircle style={{ fontSize: 24, color: "#ffffff" }} />
          </div>
          <div className="sidebar-user-meta">
            <span className="sidebar-user-name">{data?.name || "User"}</span>
            <span className="sidebar-user-role">{roleName || "Staff"}</span>
            {branchName && <span className="sidebar-user-branch">{branchName}</span>}
          </div>
          <svg className="sidebar-user-chevron" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ width: 14, height: 14 }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div> */}

        {/* Logout */}
        <button
          className="sidebar-logout-btn"
          onClick={logout}
          title="Logout"
        >
          <ExitToAppIcon className="text-[20px] text-danger" />
          <span>Logout</span>
        </button>
      </div>
    </Sidebar>
  );
};

export default SidebarPro;